import { Moon, Sun } from "lucide-react";
import { useCallback, useState } from "react";
import { cn } from "./cn";

export type ThemePreference = "light" | "dark";

/** Shared with the boot script in index.html — keep the two in step. */
const STORAGE_KEY = "theme-preference";

const OPTIONS: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
];

function prefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * The stored choice, or the OS as the opening bid.
 *
 * There used to be a third option, "System", which UAT could not tell apart from
 * "Light" — it looks identical to whichever of the two the OS is already on, so
 * it reads as a duplicate of one of its neighbours. What it was actually for
 * survives here: the OS still decides where a first-time visitor starts, so a
 * dark laptop lands dark. It is a seed, not a mode — nothing follows the OS
 * after the user has an opinion. A stored "system" from before falls into the
 * same seed, so it needs no migration.
 */
function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    /* Private mode can throw. Fall through. */
  }
  return prefersDark() ? "dark" : "light";
}

function applyToDocument(preference: ThemePreference) {
  document.documentElement.dataset.theme = preference;
}

/**
 * Theme preference, persisted.
 *
 * `data-theme` on <html> is always concrete — "light" or "dark", never absent —
 * because the boot script resolves it before first paint. That is what lets
 * styles.css carry one dark block instead of a media query *and* an attribute
 * rule.
 */
export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(readPreference);

  const choose = useCallback((next: ThemePreference) => {
    setPreference(next);
    applyToDocument(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* Preference just won't survive the session. Not worth surfacing. */
    }
  }, []);

  return { preference, setPreference: choose };
}

/**
 * Segmented control, not a cycling button.
 *
 * A control whose accessible name has to read "switch to dark" while showing a
 * sun icon is a riddle. A radiogroup says what the options are and which one is
 * current.
 */
export function ThemeToggle({ className = "" }: { className?: string }) {
  const { preference, setPreference } = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className={cn(
        "inline-flex items-center gap-0.5 rounded-lg border border-line bg-surface-sunken p-0.5",
        className
      )}
    >
      {OPTIONS.map(({ value, label, icon: Icon }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={label}
            title={label}
            onClick={() => setPreference(value)}
            className={cn(
              "grid h-7 w-7 place-items-center rounded-md transition-colors",
              active
                ? "bg-surface text-text ring-1 ring-line-strong"
                : "text-subtle hover:text-text"
            )}
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        );
      })}
    </div>
  );
}
